import Layout from "../Layout/Layout";
import { useEffect, useState } from "react";
import { getPages } from "./utilities/ApiCalls";
import { Helmet } from "react-helmet-async";
import { setPageTitle } from "../utils/titleUtils";
import { useLocation } from "react-router-dom";
import styled from "styled-components";
import { BookOpen, Mic, Trophy, Users, Award, PlayCircle } from "lucide-react";

export default function WhatWeDo() {
  const location = useLocation();
  const defaultWhat = {
    title: "What We Do",
    content: "At StoryTent, we serve the realms where virtual and in-reality creativity converge, fostering a vibrant community bound by the love for storytelling. Through our innovative online portal and immersive offline open mic events under our brand Koral, we provide a dynamic platform for storytellers, poets, and readers alike to bridge the gap between their artistic expression and recognition."
  };
  const [about, setAbout] = useState(defaultWhat);

  useEffect(() => {
    setPageTitle(location.pathname);
    getPages({ 'title': 'what we do' }).then((response) => {
      if (response && response.data && response.data.content) {
        setAbout(response.data);
      }
    }).catch((error) => {
      console.error("Error fetching what-we-do content:", error);
    });
  }, [location]);

  return (
    <Layout>
      <Helmet>
        <title>What We Do | StoryTent</title>
        <meta name="description" content="Discover what we do at StoryTent: empowering creators to publish stories and poems, host open mics, and connect." />
        <meta property="og:title" content="What We Do | StoryTent" />
        <meta property="og:description" content="Discover what we do at StoryTent: empowering creators to publish stories and poems, host open mics, and connect." />
        <meta property="og:url" content="https://www.storytent.in/what-we-do" />
        <link rel="canonical" href="https://www.storytent.in/what-we-do" />
      </Helmet>

      <PageWrapper>
        <div className="container">
          <HeroCard>
            <IconBadge>
              <BookOpen size={30} color="#2dd4bf" />
            </IconBadge>
            <MainTitle>{about.title || "What We Do"}</MainTitle>
            <HeroDescription>{about.content || defaultWhat.content}</HeroDescription>
          </HeroCard>

          <OfferingsGrid>
            <OfferingCard>
              <OfferingIconWrap>
                <BookOpen size={24} color="#2dd4bf" />
              </OfferingIconWrap>
              <OfferingTitle>Story & Poem Publishing</OfferingTitle>
              <OfferingText>
                A rich digital canvas where authors and poets can publish, customize layouts, format typography, and reach engaged readers worldwide.
              </OfferingText>
            </OfferingCard>

            <OfferingCard>
              <OfferingIconWrap>
                <Mic size={24} color="#f43f5e" />
              </OfferingIconWrap>
              <OfferingTitle>Live Open Mics & Koral</OfferingTitle>
              <OfferingText>
                Online and offline open mic sessions where creators perform live, share their spoken word artistry, and build authentic audience connections.
              </OfferingText>
            </OfferingCard>

            <OfferingCard>
              <OfferingIconWrap>
                <Trophy size={24} color="#f59e0b" />
              </OfferingIconWrap>
              <OfferingTitle>Literary Contests & Challenges</OfferingTitle>
              <OfferingText>
                Compete in themed writing contests, gain recognition from literary judges, and win awards and cash prizes.
              </OfferingText>
            </OfferingCard>

            <OfferingCard>
              <OfferingIconWrap>
                <Users size={24} color="#8b5cf6" />
              </OfferingIconWrap>
              <OfferingTitle>Creator Community</OfferingTitle>
              <OfferingText>
                Follow favorite authors, like and share captivating works, exchange feedback, and join meaningful literary discussions.
              </OfferingText>
            </OfferingCard>
          </OfferingsGrid>
        </div>
      </PageWrapper>
    </Layout>
  );
}

const PageWrapper = styled.div`
  background: transparent;
  color: var(--color-text, #ffffff);
  padding: 30px 0 60px;
  min-height: 80vh;
`;

const HeroCard = styled.div`
  background: var(--color-surface, #141f1e);
  border: 1px solid var(--color-border, rgba(255, 255, 255, 0.1));
  border-radius: 16px;
  padding: 36px 30px;
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.18);
  text-align: center;
  max-width: 920px;
  margin: 0 auto 36px;
`;

const IconBadge = styled.div`
  width: 58px;
  height: 58px;
  border-radius: 50%;
  background: rgba(45, 212, 191, 0.12);
  border: 1px solid rgba(45, 212, 191, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 18px;
`;

const MainTitle = styled.h1`
  color: var(--color-text, #ffffff);
  font-size: 2.2rem;
  font-weight: 700;
  margin-bottom: 16px;

  @media (max-width: 768px) {
    font-size: 1.7rem;
  }
`;

const HeroDescription = styled.p`
  color: var(--color-text-secondary, #cbd5e1);
  font-size: 1.12rem;
  line-height: 1.8;
  max-width: 800px;
  margin: 0 auto;
  text-align: justify;

  @media (max-width: 768px) {
    font-size: 1rem;
    text-align: left;
  }
`;

const OfferingsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  max-width: 920px;
  margin: 0 auto;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const OfferingCard = styled.div`
  background: var(--color-surface, #141f1e);
  border: 1px solid var(--color-border, rgba(255, 255, 255, 0.1));
  border-radius: 14px;
  padding: 24px 22px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.12);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  transition: transform 0.2s ease, border-color 0.2s ease;

  &:hover {
    transform: translateY(-3px);
    border-color: rgba(45, 212, 191, 0.3);
  }
`;

const OfferingIconWrap = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.05);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 14px;
`;

const OfferingTitle = styled.h2`
  font-size: 1.15rem;
  font-weight: 600;
  color: var(--color-text, #ffffff);
  margin-bottom: 8px;
`;

const OfferingText = styled.p`
  font-size: 0.95rem;
  color: var(--color-text-secondary, #94a3b8);
  line-height: 1.6;
  margin: 0;
`;